import { ordinalSuffix } from './shared_helpers'

export const stdTimezoneOffset = () => {
    let jan = new Date(new Date().getFullYear(), 0, 1)
    let jul = new Date(new Date().getFullYear(), 6, 1)
    return Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset())
}

export const getTimeZoneOffset = () => {
    let date = new Date()
    let offset = date.getTimezoneOffset()
    let offset_official = (offset < stdTimezoneOffset() ? offset + 60 : offset) * 60
    return offset_official > 0 ? offset_official * -1 : offset_official
}

export const getTime = date => {
    let conv = date * 1000
    let newDate = new Date(conv)
    let hour = newDate.getHours()
    let minutes = newDate.getMinutes()
    let zeroMins = (minutes < 10 ? '0' : '') + minutes
    let ampm = hour >= 12 ? 'PM' : 'AM'
    if (hour > 12) {
        hour -= 12
    } else if (hour === 0) {
        hour = 12
    }

    let time = `${hour}:${zeroMins} ${ampm} ET`
    return time
}

/*
 * getTimezone: This function returns a name of a timezone.
 *
 * @return {string} timezone - Name of the timezone or empty string
 */
export const getTimezone = () => {
    const user_offset = getTimeZoneOffset()
    const timezones = {
        3.5: 'NT',
        4: 'AT',
        5: 'ET',
        6: 'CT',
        7: 'MT',
        8: 'PT',
    }
    return timezones[Math.abs(Math.floor(user_offset / 3600))] || ''
}

/*
 * getMonthName: This function returns a name of a month based on its numeric value.
 * Either long or short name e.g. Jan vs January
 *
 * @param {int} monthNum - integer corresponding to a month
 * @param {boolean} short - Indicate with long or short name should be returned. Default => long
 * @return {string} month - Name of the month
 */
export const getMonthName = (monthNum, long = true) => {
    const months = long
        ? [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ]
        : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    return months[monthNum]
}

const monthsMap = new Map()
monthsMap.set('Jan', 0)
monthsMap.set('Feb', 1)
monthsMap.set('Mar', 2)
monthsMap.set('Apr', 3)
monthsMap.set('May', 4)
monthsMap.set('Jun', 5)
monthsMap.set('Jul', 6)
monthsMap.set('Aug', 7)
monthsMap.set('Sep', 8)
monthsMap.set('Oct', 9)
monthsMap.set('Nov', 10)
monthsMap.set('Dec', 11)

/*
 * getMonthNumber: This function returns month number
 *
 * @param {string} format - name of the month
 * @return {number} the returned month number
 */

export const getMonthNumber = monthName => {
    const monthNo = monthsMap.get(monthName)
    return monthNo
}

/*
 * getDatetime: This function returns date/time based on a timestamp
 *
 * @param {int} timestamp - integer corresponding to a game's timestamp
 * @param {string} format - Wh
 * @return {object} datetime - hours, minutes, date, month, day
 */
export const getDatetime = (timestamp, format = 'raw') => {
    const game_time = new Date(timestamp * 1000)

    const datetimeObject = {
        hours_24: game_time.getHours(),
        hours_12: ((game_time.getHours() + 11) % 12) + 1,
        minutes: ('0' + game_time.getMinutes()).substr(-2),
        date: game_time.getDate(),
        date_w_zero: game_time.getDate() < 10 ? '0' + game_time.getDate() : game_time.getDate(),
        month_num: game_time.getMonth() + 1,
        month_num_w_zero:
            game_time.getMonth() + 1 < 10
                ? '0' + (game_time.getMonth() + 1)
                : game_time.getMonth() + 1,
        month: getMonthName(game_time.getMonth(), false),
        month_full: getMonthName(game_time.getMonth(), true),
        day: getDayOfWeek(game_time.getDay(), false),
        day_full: getDayOfWeek(game_time.getDay(), true),
        year: game_time.getFullYear(),
    }
    const meridiem = datetimeObject.hours_24 >= 12 ? 'PM' : 'AM'

    switch (format) {
        case 'raw':
            return datetimeObject
        case 'date':
            return datetimeObject.day + ', ' + datetimeObject.month + ' ' + datetimeObject.date
        case 'full-day':
            return (
                datetimeObject.day_full +
                ', ' +
                datetimeObject.month_full +
                ' ' +
                datetimeObject.date
            )
        case 'date-nocomma':
            return datetimeObject.day + ' ' + datetimeObject.month + ' ' + datetimeObject.date
        case 'dashed':
            let month =
                game_time.getMonth() + 1 < 10 && game_time.getMonth() + 1 >= 0
                    ? '0' + (game_time.getMonth() + 1)
                    : game_time.getMonth() + 1
            let day =
                game_time.getDate() < 10 && game_time.getDate() >= 0
                    ? '0' + game_time.getDate()
                    : game_time.getDate()
            return game_time.getFullYear() + '-' + month + '-' + day
        case 'time':
            return (
                datetimeObject.hours_12 +
                ':' +
                datetimeObject.minutes +
                ' ' +
                meridiem +
                ' ' +
                getTimezone()
            )
        case 'time_wo_timezone':
            return datetimeObject.hours_12 + ':' + datetimeObject.minutes + ' ' + meridiem
        case 'date-time':
            return (
                datetimeObject.month +
                ' ' +
                datetimeObject.date +
                ', ' +
                datetimeObject.hours_12 +
                ':' +
                datetimeObject.minutes +
                ' ' +
                meridiem +
                ' ' +
                getTimezone()
            )
        case 'day-date-time':
            return (
                datetimeObject.day +
                ' ' +
                datetimeObject.month +
                ' ' +
                datetimeObject.date +
                ' / ' +
                datetimeObject.hours_12 +
                ':' +
                datetimeObject.minutes +
                ' ' +
                meridiem +
                ' ' +
                getTimezone()
            )
        case 'month/day':
            return (
                ('0' + datetimeObject.month_num).slice(-2) +
                '/' +
                ('0' + datetimeObject.date).slice(-2)
            )
        case 'month.day':
            return (
                datetimeObject.month +
                ' ' +
                datetimeObject.date
            )
        default:
            return datetimeObject
    }
}

/*
 * getDayOfWeek: This function returns a name of a weekday based on its numeric value.
 * Either long or short name e.g. Sun vs Sunday
 *
 * @param {int} int - integer corresponding to a day
 * @param {boolean} short - Indicate with long or short name should be returned. Default => long
 * @return {string} day - Name of the day
 */
export const getDayOfWeek = (int, long = true) => {
    var days = long
        ? ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
        : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    return days[int]
}

export const timeOfWeek = int => {
    return int === 0 || int === 6 ? 'Weekend' : 'Week day'
}

/*
 * reformatDashedDate: This function takes a dashed date and returns the proper long formate
 *
 * @param {dashed_date} string - year-month-day string
 * @return {string} full day full month date
 */
export const reformatDashedDate = dashed_date => {
    if (!dashed_date) return ''

    dashed_date = dashed_date.replace(/-/g, '/')

    let utc_timestamp = new Date(dashed_date + ' 00:00:00').getTime() / 1000

    return getDatetime(utc_timestamp, 'date-nocomma')
}

/*
 * getStartOfDayUnix: This function returns the unix timestamp for the start of the current day
 *
 * @return {number} timestamp in seconds
 */
export const getStartOfDayUnix = () => {
    let now = new Date()
    let startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    return startOfDay.getTime() / 1000
}

/*
 * testDashedDateFormat: This function returns the date argument if valid - else it throws an error
 *
 * @param {date} string - dashed date in v2 stats format
 * @return {string} date
 */
export const testDashedDateFormat = date => {
    let date_regex = /^(\d{4})(-)(\d{2})(-)(\d{2})$/
    if (date_regex.test(date)) {
        return date
    } else {
        alert('Incorrect Date Format in URL')
        throw new Error('Incorrect Date Format in URL')
    }
}

/*
 * isToday: Check if given timestamp belongs to the current day (today)
 *
 * @param {number} timestamp - timestamp
 * @return {boolean} isToday
 */
export const isToday = timestamp => {
    const today = new Date()
    const today_obj = getDatetime(today.getTime() / 1000)
    const check_obj = getDatetime(timestamp)

    return (
        today_obj.date === check_obj.date &&
        today_obj.month === check_obj.month &&
        today_obj.year === check_obj.year
    )
}

/*
 * getDayAndDayNumber: Returns the day from timestamp in this format Saturday 2nd
 * @param {number} timestamp - timestamp
 * @return {boolean} long or short format of the day string
 */
export const getDayAndDayNumber = (timestamp, long = true, suffix = true) => {
    const date = new Date(timestamp * 1000)
    const dayName = getDayOfWeek(date.getDay(), long)
    const dayNumber = suffix ? ordinalSuffix(date.getDate()) : date.getDate()
    return dayName + ' ' + dayNumber
}

/*
 * getDayAndDayNumberFromDate: Returns the day from date and time in this format Saturday 2nd
 * @param {Date} dateTime - dateTime
 * @return {boolean} long or short format of the day string
 */
export const getDateFormatedFromDate = (date, long = true) => {
    const monthName = getMonthName(date.getMonth(), long)
    const dayNumber = date.getDate()
    const year = date.getFullYear()
    return monthName + ' ' + dayNumber + ', ' + year
}

/*
 * getHoursMinutesAndSecondsFromTimestamp: returns time in format: 00:00:00 from timestamp
 * @param {int} time
 * @return {string}
 */
export const getHoursMinutesAndSecondsFromTimestamp = time => {
    var hrs = ~~(time / 3600)
    var mins = ~~((time % 3600) / 60)
    var secs = ~~time % 60

    var ret = ''

    if (hrs > 0) {
        ret += '' + hrs + ':' + (mins < 10 ? '0' : '')
    }

    ret += '' + mins + ':' + (secs < 10 ? '0' : '')
    ret += '' + secs
    return ret
}
